import type { MealSlot } from "@shared/types";

/** Time-of-day → default meal slot. The caller passes the local hour (0–23)
 *  so this stays pure and testable without reading the wall clock. */
export function defaultMealSlot(hour: number): MealSlot {
  if (hour >= 4  && hour <= 10) return "breakfast";
  if (hour >= 11 && hour <= 15) return "lunch";
  if (hour >= 16 && hour <= 21) return "dinner";
  return "snack"; // 22–23 and 0–3
}

export function todayLocal(): string {
  const d = new Date();
  return [
    d.getFullYear(),
    String(d.getMonth() + 1).padStart(2, "0"),
    String(d.getDate()).padStart(2, "0"),
  ].join("-");
}

/**
 * Which day to show after the calendar date has moved on — the app was left
 * open across midnight, or backgrounded overnight.
 *
 * Someone sitting on today follows the clock: without this the first meal of
 * the new morning is logged against yesterday. Someone who deliberately opened
 * last Tuesday stays on last Tuesday; yanking them forward loses their place.
 *
 * Pure so the rollover is testable without waiting for midnight.
 */
export function rolledOverDate(current: string, knownToday: string, now: string): string {
  if (now === knownToday) return current;          // nothing has changed
  return current === knownToday ? now : current;
}

export function addDays(dateStr: string, n: number): string {
  const [y, m, d] = dateStr.split("-").map(Number);
  const dt = new Date(y, m - 1, d);
  dt.setDate(dt.getDate() + n);
  return [
    dt.getFullYear(),
    String(dt.getMonth() + 1).padStart(2, "0"),
    String(dt.getDate()).padStart(2, "0"),
  ].join("-");
}

/** "Today" / "Yesterday" / a weekday name — a bare ISO date is noise in a
 *  sentence. `reference` is the day being looked at, which is not necessarily
 *  the real today: browsing back to last Tuesday, "yesterday" has to mean the
 *  Monday before it, not the one before now. */
export function relativeDayLabel(
  iso: string, reference: string, t: (k: string) => string,
): string {
  if (iso === reference) return t("date.today");
  if (iso === addDays(reference, -1)) return t("date.yesterday");
  const [y, m, d] = iso.split("-").map(Number);
  return t(`day.short.${new Date(y, m - 1, d).getDay()}`);
}

/** The user-local calendar day of a unix timestamp (seconds) — e.g. the day
 *  an account was created, for the timeline floor (shared/timeline.ts). */
export function unixToLocalDay(unixSec: number): string {
  const d = new Date(unixSec * 1000);
  const p2 = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${p2(d.getMonth() + 1)}-${p2(d.getDate())}`;
}
